import {Helpers} from "../../../common/js/Helpers";
import {postWithToken} from "../utils/formToken";

/**
 * The cart page.
 *
 * Every change to a row goes to the server right away; the answer carries
 * the rows and the summary rendered anew, which replace the ones on the
 * page. So there is no "please recalculate" state any more and no submit
 * for a single quantity.
 *
 * The events are handled on the list, not on the rows: a replaced row has
 * no listeners of its own and needs none.
 */

const ROW_SELECTOR = '.cart-item';
const SUMMARY_SELECTOR = '.cart-summary';
const ERROR_SELECTOR = '.eventgallery-cart-error';
const NOTICE_SELECTOR = '.eventgallery-cart-notice';
const STATUS_SELECTOR = '.eventgallery-cart-status';
const UPDATING_CLASS = 'is-updating';
/** The quantity of a field which was sent to the server and whose answer has not replaced the row yet. */
const SENT_ATTRIBUTE = 'data-eg-sent';

/**
 * The controls of a row which can have the focus when the row is put on the page anew, found
 * again by their name or by their hook class.
 */
const FOCUS_HOOKS = ['.clone', '.add-comment', '.delete-lineitem', '.scalepriceinformation', '.open-event'];

/** Number of requests on their way to the server. */
let pendingRequests = 0;

/** A form which was submitted while a request was still running. */
let formToSubmit = null;
let submitterToUse = null;

function configuration() {
    return window.EventGalleryCartConfiguration || {};
}

function rowOf(element) {
    return Helpers.getParent(element, ROW_SELECTOR);
}

function lineItemIdOf(row) {
    return row ? row.getAttribute('data-lineitemid') : null;
}

function setUpdating(element, updating) {
    if (element) {
        element.classList.toggle(UPDATING_CLASS, updating);
    }
}

function showError(show) {
    const box = document.querySelector(ERROR_SELECTOR);
    if (box) {
        box.hidden = !show;
    }
}

/**
 * The server did not take a change, for example a format which is no longer
 * offered; the row of the answer shows what the cart holds now.
 */
function showNotice(text) {
    const box = document.querySelector(NOTICE_SELECTOR);
    if (box && text) {
        // into the body of the eg-notice, so its icon stays; onto the box of an older markup
        (box.querySelector('.eg-notice__body') || box).textContent = text;
        box.hidden = false;
    }
}

/**
 * Sends one change and puts the answer onto the page. The change carries the form token of the
 * session; a refused token is renewed and the change sent once more (utils/formToken.ts).
 *
 * @param url the cart task to post to
 * @param params the fields to post
 * @param row the row the change belongs to, marked busy while the request runs
 * @param revert called if the request fails, so the field shows the old value again
 */
function post(url, params, row, revert) {
    pendingRequests++;
    setUpdating(row, true);
    setUpdating(document.querySelector(SUMMARY_SELECTOR), true);
    showError(false);

    return postWithToken(url, {body: new URLSearchParams(params)})
        .then((response) => {
            if (!response.ok) {
                throw new Error('The cart request failed with HTTP ' + response.status);
            }
            return response.json();
        })
        .then((data) => applyResponse(data))
        .catch((error) => {
            // eslint-disable-next-line no-console
            console.error(error);
            showError(true);
            setUpdating(row, false);
            setUpdating(document.querySelector(SUMMARY_SELECTOR), false);
            if (revert) {
                revert();
            }
        })
        .then(() => {
            pendingRequests--;
            if (pendingRequests === 0) {
                submitPendingForm();
            }
        });
}

/**
 * Where the focus is inside a row, so it can go to the same control of the row the server
 * rendered anew: the row and the name of the control, or its hook class.
 */
function focusInRows(list) {
    const active = document.activeElement;
    if (!active || !list.contains(active)) {
        return null;
    }
    const row = rowOf(active);
    if (!row) {
        return null;
    }
    let selector = null;
    if (active.name && /^(quantity|type|comment)_/.test(active.name)) {
        selector = '[name="' + active.name + '"]';
    } else {
        selector = FOCUS_HOOKS.find((hook) => active.matches(hook)) || null;
    }
    const rows = Array.from(list.querySelectorAll(ROW_SELECTOR));
    return {id: lineItemIdOf(row), selector: selector, index: rows.indexOf(row)};
}

/**
 * Puts the focus back where it was before the rows were replaced: on the same control of the
 * same row; on the quantity of the row which took the place of a removed one, or of the last
 * row. Until 7.0.0 the focus fell back to the page, so a keyboard or a screen reader started
 * at the top after every change of a quantity (SHOP-8).
 */
function restoreFocus(list, focus) {
    if (!focus) {
        return;
    }
    let target = null;
    const row = list.querySelector(ROW_SELECTOR + '[data-lineitemid="' + focus.id + '"]');
    if (row && focus.selector) {
        target = row.querySelector(focus.selector);
    }
    if (!target) {
        const rows = list.querySelectorAll(ROW_SELECTOR);
        const next = rows[Math.min(Math.max(focus.index, 0), rows.length - 1)];
        target = next ? next.querySelector('input.eventgallery-quantity') : null;
    }
    if (target && typeof target.focus === 'function') {
        target.focus({preventScroll: true});
    }
}

/**
 * The scale prices a buyer opened with *All tiers* (the ids of the open `.scaleprices`), so they
 * stay open in the rows the server rendered anew: a change of a quantity renders every row, and
 * the buyer who watches the tiers while changing the quantity would lose them each time.
 */
function openScalePrices(list) {
    return Array.from(list.querySelectorAll('.scaleprices'))
        .filter((part) => part.id && !part.hidden && !part.classList.contains('zero-height'))
        .map((part) => part.id);
}

function reopenScalePrices(ids) {
    ids.forEach((id) => {
        const part = document.getElementById(id);
        const button = document.querySelector('.scalepriceinformation[aria-controls="' + id + '"]');
        if (part && button) {
            part.hidden = false;
            button.setAttribute('aria-expanded', 'true');
        }
    });
}

/**
 * Says what the change did in the live region of the page (an eg-sr-only role="status"), so a
 * screen reader hears the new total after a change of a quantity, a format or a copy. The
 * sentence is the data-template of the region with {total}.
 */
function announce(data) {
    const status = document.querySelector(STATUS_SELECTOR);
    if (!status || !data.summary || !data.summary.total) {
        return;
    }
    const template = status.getAttribute('data-template') || '{total}';
    status.textContent = template.replace('{total}', data.summary.total);
}

/**
 * Replaces the rows and the summary with the ones the server rendered.
 * Rows the answer does not carry any more are gone from the cart; rows the
 * page does not have yet (the copy of a row) are added at the end, where a
 * reload would show them.
 */
function applyResponse(data) {
    const rows = data.rows || {};
    const list = document.querySelector('.eventgallery-cart .cart-items');
    if (!list) {
        return;
    }
    const focus = focusInRows(list);
    const openTiers = openScalePrices(list);

    const onPage = {};
    let remaining = 0;
    Helpers.forEach(list.querySelectorAll(ROW_SELECTOR), (row) => {
        const id = lineItemIdOf(row);
        if (id && Object.prototype.hasOwnProperty.call(rows, id)) {
            row.outerHTML = rows[id];
            onPage[id] = true;
            remaining++;
        } else {
            row.remove();
        }
    });
    Object.keys(rows).forEach((id) => {
        if (!onPage[id]) {
            list.insertAdjacentHTML('beforeend', rows[id]);
            remaining++;
        }
    });

    if (remaining === 0) {
        // the server shows the empty cart page
        window.location.reload();
        return;
    }

    const summary = document.querySelector(SUMMARY_SELECTOR);
    if (summary && data.summaryHtml) {
        summary.outerHTML = data.summaryHtml;
    }

    reopenScalePrices(openTiers);
    restoreFocus(list, focus);
    showNotice(data.notice);
    announce(data);

    // the mini cart listens for this, it lives in the other bundle
    document.dispatchEvent(new CustomEvent('eventgallery-minicart-reload', {detail: {cart: data.cart, summary: data.summary}}));
    document.dispatchEvent(new CustomEvent('eventgallery-images-added'));
}

function updateLineItem(row, params, revert) {
    const id = lineItemIdOf(row);
    if (!id) {
        return;
    }
    params.lineitemid = id;
    post(configuration().updateLineItemUrl, params, row, revert);
}

function removeAllItems(e) {
    e.preventDefault();
    if (confirm(configuration().removeAllItemsWarning) === false) {
        return;
    }
    post(configuration().clearCartUrl, {}, null, null);
}

/**
 * "Continue to Checkout" while a change is still on its way: the checkout
 * would read the cart before the change is stored. The submit waits for the
 * last answer.
 */
function onSubmit(e) {
    if (pendingRequests === 0) {
        return;
    }
    e.preventDefault();
    formToSubmit = e.target;
    submitterToUse = e.submitter || null;
}

function submitPendingForm() {
    if (!formToSubmit) {
        return;
    }
    const form = formToSubmit;
    const submitter = submitterToUse;
    formToSubmit = null;
    submitterToUse = null;
    if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitter || undefined);
    } else {
        form.submit();
    }
}

/**
 * Sends the quantity of a field of a row, once: Enter sends it (onKeydown()), and the change event
 * the browser fires when the field loses its focus afterwards finds it sent already
 * (data-eg-sent). Enter in a field whose quantity is the one of the row sends nothing
 * (onlyWhenChanged); a change event sends what the field holds, as it always did.
 */
function changeQuantity(input, onlyWhenChanged = false) {
    const row = rowOf(input);
    if (!row) {
        return;
    }
    let quantity = parseInt(input.value);
    if (isNaN(quantity) || quantity < 0) {
        quantity = 0;
    }
    const sent = input.getAttribute(SENT_ATTRIBUTE);
    if (sent === String(quantity) || (onlyWhenChanged && sent === null && String(quantity) === input.defaultValue)) {
        return;
    }
    input.setAttribute(SENT_ATTRIBUTE, String(quantity));
    const previous = input.defaultValue;
    updateLineItem(row, {quantity: quantity}, () => {
        input.value = previous;
        input.removeAttribute(SENT_ATTRIBUTE);
    });
}

/**
 * Enter in the quantity field of a row applies the change and nothing else (the maintainer's
 * answer 75). Without this the browser submitted the form through its default button (updateCart)
 * once the change had gone out: the page reloaded, the tiers a buyer had opened closed and the
 * focus started at the top of the page. The field keeps the focus; the row the server renders anew
 * gets it back (restoreFocus()).
 */
function onKeydown(e) {
    const target = e.target;
    if (e.key !== 'Enter' || e.isComposing || !target || typeof target.matches !== 'function' || !target.matches('input.eventgallery-quantity')) {
        return;
    }
    e.preventDefault();
    changeQuantity(target, true);
}

function onChange(e) {
    const target = e.target;
    const row = rowOf(target);
    if (!row) {
        return;
    }

    if (target.matches('input.eventgallery-quantity')) {
        changeQuantity(target);
    } else if (target.matches('select.imagetype')) {
        const previous = Array.prototype.find.call(target.options, (option) => option.defaultSelected);
        updateLineItem(row, {imagetypeid: target.value}, () => { if (previous) target.value = previous.value; });
    } else if (target.matches('textarea[name^="comment_"]')) {
        updateLineItem(row, {comment: target.value}, null);
    }
}

function onClick(e) {
    const target = e.target;

    const remove = target.closest('.delete-lineitem');
    if (remove) {
        e.preventDefault();
        updateLineItem(rowOf(remove), {quantity: 0}, null);
        return;
    }

    const clone = target.closest('.clone');
    if (clone) {
        e.preventDefault();
        const row = rowOf(clone);
        post(configuration().cloneLineItemUrl, {lineitemid: lineItemIdOf(row)}, row, null);
        return;
    }

    const scalePrice = target.closest('.scalepriceinformation');
    if (scalePrice) {
        e.preventDefault();
        toggle(scalePrice, controlledBy(scalePrice, () => Helpers.getParent(scalePrice, 'div').querySelector('div.scaleprices')));
        return;
    }

    const comment = target.closest('.add-comment');
    if (comment) {
        e.preventDefault();
        const area = controlledBy(comment, () => rowOf(comment).querySelector('.comment-area'));
        if (toggle(comment, area)) {
            const field = area.querySelector('textarea');
            if (field) {
                field.focus();
            }
        }
    }
}

/**
 * The part a toggle opens: the element its aria-controls names, or the one an older markup
 * finds by its class.
 */
function controlledBy(button, fallback) {
    const id = button.getAttribute('aria-controls');
    return (id && document.getElementById(id)) || fallback();
}

/**
 * Opens or closes the price list or the comment of a row: the part gets or loses `hidden`, the
 * button says so in aria-expanded. An older markup closes the part with the class zero-height,
 * which goes at the first toggle.
 *
 * @returns {boolean} whether the part is open now
 */
function toggle(button, part) {
    if (!part) {
        return false;
    }
    const open = part.hidden || part.classList.contains('zero-height');
    part.classList.remove('zero-height');
    part.hidden = !open;
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    return open;
}

document.addEventListener('DOMContentLoaded', () => {
    const cart = document.querySelector('.eventgallery-cart');
    if (!cart) {
        return;
    }

    cart.addEventListener('change', onChange);
    cart.addEventListener('keydown', onKeydown);
    cart.addEventListener('click', onClick);

    Helpers.forEach(document.querySelectorAll('.eventgallery-removeAll'), (elm) => elm.addEventListener('click', removeAllItems));
    Helpers.forEach(document.querySelectorAll('form.cart-form'), (form) => form.addEventListener('submit', onSubmit));
});
